import { JSX, useState } from 'react';
import { useParams } from 'react-router-dom';
import Back from 'components/Back';
import IterationPageHeader from 'components/IterationPageHeader';
import Alert from 'components/Alert';
import useCampaign from 'hooks/useCampaign';
import useCampaignNavigate from 'hooks/useCampaignNavigate';
import useUser from 'hooks/useUser.ts';
import useUserAuthorisation from 'hooks/useUserAuthorisation';
import { IIterationLevelParams } from 'utils/parameterUtils';
import { Button, Card, Radios } from 'nhsuk-react-components';
import { getIterationDetailsById } from 'utils/iterationUtils';
import useRunRequest from 'queries/mutate/runRequest';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema';
import { API_SUBMIT_CONFIG_ERROR } from 'constants/errorMessages';

function IterationRunTypePage() {
  const { teamId, campaignId, iterationId } = useParams() as IIterationLevelParams;
  const { campaign } = useCampaign();
  const user = useUser();
  const { canUpdateCampaign } = useUserAuthorisation();
  const { navigateToCampaign } = useCampaignNavigate();
  const [dryRun, setDryRun] = useState<boolean | undefined>();
  const [showRunTypeError, setShowRunTypeError] = useState(false);
  const [errorTitle, setErrorTitle] = useState('Summary report not available');
  const [errorMessage, setErrorMessage] = useState<string | JSX.Element | null>(null);
  const [errorButtonText, setErrorButtonText] = useState('Close');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const runRequest = useRunRequest();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });

  const currentIteration = campaign?.CampaignConfig?.Iterations?.find(
    (iteration) => iteration.ID === iterationId,
  );
  const iterationDetails = campaign ? getIterationDetailsById(campaign, iterationId) : undefined;
  const canShowRequestButton = iterationDetails?.status === ITERATION_STATUS.APPROVED
    && canUpdateCampaign()
    && user.roles.includes(`${teamId}:release`);

  const handleRunTypeChange = (value: boolean) => {
    setDryRun(value);
    setShowRunTypeError(false);
  };

  const handleSubmitConfig = async () => {
    try {
      if (dryRun === undefined) {
        setShowRunTypeError(true);
        return;
      }

      setIsSubmitting(true);
      await runRequest.mutateAsync({
        teamId,
        iterationId,
        campaignId,
        isDryRun: dryRun,
      });
      await fetchSpecificCampaign.refetch();
      navigateToCampaign(`iteration/${iterationId}/summary-report-requested`);
    } catch (e) {
      if (e instanceof Error && e.message === 'A selected cohort has been withdrawn') {
        setErrorTitle('Cohort(s) removed');
        setErrorMessage(
          <>
            <p>One or more of your cohorts is no longer available.</p>
            <p className="nhsuk-u-margin-bottom-0"><b>What you need to do next</b></p>
            <ol className="nhsuk-u-margin-bottom-0">
              <li className="nhsuk-u-margin-bottom-0">Delete any cohorts which are &apos;removed from team&apos;.</li>
              <li className="nhsuk-u-margin-bottom-0">Review all your cohorts and change if necessary.</li>
              <li className="nhsuk-u-margin-bottom-0">Review all your rules, as they may be affected, and change if necessary.</li>
            </ol>
          </>,
        );
        setErrorButtonText('I understand');
        return;
      }

      setErrorTitle('Summary report not available');
      setErrorMessage(API_SUBMIT_CONFIG_ERROR);
      setErrorButtonText('Close');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="iteration-reviewers-page">
      <div className="nhsuk-u-margin-bottom-6">
        <Back
          label="Iteration summary"
          backTo={`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary`}
        />
      </div>
      {currentIteration && (
        <IterationPageHeader
          iteration={currentIteration}
          title="Request summary report"
        />
      )}

      <Card>
        <>
          <h4>Send for live or dry run</h4>

          <p>Before you can request a summary report, you&apos;ll have to select either a &apos;live run&apos; or a &apos;dry run&apos;.</p>
          <p>A live run can be sent to NHS Notify after the summary report is reviewed and approved.</p>

          <p>A dry run can help you check the numbers of communications a run generates. A dry run cannot be sent to NHS Notify.</p>

          <Radios
            onChange={(e) => handleRunTypeChange((e.target as HTMLInputElement).value === 'true')}
            name="runType"
            inline
            error={showRunTypeError ? "Select either 'Live run' or 'Dry run'" : undefined}
          >
            <Radios.Item value="false">Live run</Radios.Item>
            <Radios.Item value="true">Dry run</Radios.Item>
          </Radios>

        </>
      </Card>

      {canShowRequestButton && (
        <>
          <Button onClick={handleSubmitConfig} disabled={isSubmitting}>
            {isSubmitting ? 'Requesting...' : 'Save and continue'}
          </Button>

          {errorMessage && (
            <div className="nhsuk-u-margin-top-3 nhsuk-u-width-full">
              <Alert
                title={errorTitle}
                message={errorMessage}
                buttonText={errorButtonText}
                onClose={() => setErrorMessage(null)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default IterationRunTypePage;
